import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { actualizarEntidadSucursalId, eliminarSucursal, obtenerEntidades, registrarEntidadSu } from '../../Services/serviciohttp';
import { API_URLS } from '../../Services/servicioURL';

interface SucursalType {
    id: string;
    nombre: string;
    ip: string;
}

const Sucursales: React.FC = () => {
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [formData, setFormData] = useState({ id: '', nombre: '', ip: '' });
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [data, setData] = useState<SucursalType[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    const toast = useRef<Toast>(null);

    // Filtrar datos con useMemo para optimizar
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const searchLower = searchTerm.toLowerCase();
        return data.filter(item => 
            Object.values(item).some(val => 
                val?.toString().toLowerCase().includes(searchLower)
            )
        );
    }, [data, searchTerm]);

    // Función para mostrar mensajes
    const showMessage = (severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    // Función para limpiar formulario
    const limpiarFormulario = () => {
        setFormData({ id: '', nombre: '', ip: '' });
        setMode('view');
    };

    // Obtener sucursales
    const obtenerSucursales = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.sucursales.obtener);
            setData(result);
        } catch (error) {
            console.error('Error:', error);
            showMessage('error', 'Error', 'Error al cargar los datos');
        }
    };

    // Handlers principales
    const handleGuardar = async () => {
        const { id, nombre, ip } = formData;
        
        if (!nombre || !ip) {
            showMessage('warn', 'Campos requeridos', 'Debe completar todos los campos obligatorios');
            return;
        }

        try {
            if (mode === 'new') {
                await registrarEntidadSu(API_URLS.sucursales.registrar, id, nombre, ip);
                showMessage('success', '¡Sucursal creada!', 'La sucursal se registró exitosamente');
            } else if (mode === 'edit') {
                const confirmacion = window.confirm('⚠️ ¿Está seguro de actualizar esta sucursal?');
                if (!confirmacion) return;
                
                await actualizarEntidadSucursalId(API_URLS.sucursales.actualizar(id), id, nombre, ip);
                showMessage('success', '¡Sucursal actualizada!', 'Los cambios se guardaron correctamente');
            }
            
            limpiarFormulario();
            obtenerSucursales();
        } catch (error) {
            console.error('Error:', error);
            showMessage('error', 'Error', `No se pudo ${mode === 'new' ? 'crear' : 'actualizar'} la sucursal`);
        }
    };

    const handleEliminar = async () => {
        if (!formData.id) {
            showMessage('warn', 'Advertencia', 'Seleccione una sucursal para eliminar');
            return;
        }

        const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar esta sucursal?\n\nEsta acción no se puede deshacer.');
        if (!confirmacion) return;

        try {
            await eliminarSucursal(API_URLS.sucursales.eliminar(formData.id));
            showMessage('success', '¡Sucursal eliminada!', 'La sucursal se eliminó correctamente');
            limpiarFormulario();
            obtenerSucursales();
        } catch (error) {
            console.error('Error:', error);
            showMessage('error', 'Error', 'No se pudo eliminar la sucursal');
        }
    };

    const handleNuevo = () => {
        limpiarFormulario();
        setMode('new');
    };

    const handleEditar = () => {
        if (!formData.id) {
            showMessage('warn', 'Advertencia', 'Seleccione una sucursal para editar');
            return;
        }
        setMode('edit');
    };

    const handleConsultar = () => {
        setMostrarTabla(true);
        obtenerSucursales();
    };

    const handleSeleccionar = (sucursal: SucursalType) => {
        setFormData(sucursal);
        setMostrarTabla(false);
        setSearchTerm('');
        setMode('view');
    };

    // Función para obtener información del modo actual
    const getModeIndicator = () => {
        switch (mode) {
            case 'new': return { text: 'NUEVA SUCURSAL', color: '#10b981', icon: 'pi-plus' };
            case 'edit': return { text: 'EDITANDO SUCURSAL', color: '#f59e0b', icon: 'pi-pencil' };
            default: return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            {/* Modal de consulta */}
            {mostrarTabla && (
                <div className="overlay" onClick={() => setMostrarTabla(false)} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '700px', width: '90%', maxHeight: '80vh',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                🏢 Consultar Sucursales
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => setMostrarTabla(false)}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '12px', top: '50%',
                                    transform: 'translateY(-50%)', color: '#9ca3af'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '40px', paddingLeft: '40px',
                                        border: '2px solid #e5e7eb', borderRadius: '8px'
                                    }}
                                    placeholder="Buscar por ID, nombre o IP..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div style={{ 
                            marginBottom: '15px', fontSize: '0.9em', color: '#6b7280',
                            padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '6px'
                        }}>
                            📊 Mostrando <strong>{filteredData.length}</strong> de <strong>{data.length}</strong> sucursales
                        </div>

                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc' }}>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>🆔 ID</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>🏢 Nombre</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.length > 0 ? (
                                            filteredData.map((item, index) => (
                                                <tr 
                                                    key={index} 
                                                    onClick={() => handleSeleccionar(item)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                >
                                                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{item.id}</td>
                                                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{item.nombre}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    📭 No hay resultados
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenido principal */}
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover', backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed',
                minHeight: '100vh', position: 'relative', padding: '20px'
            }}>
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px', padding: '24px', maxWidth: '800px',
                    margin: '0 auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Header con indicador de modo */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                                🏢 Gestión de Sucursales
                            </h2>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '8px 16px', backgroundColor: modeInfo.color,
                                color: 'white', borderRadius: '20px',
                                fontSize: '0.85rem', fontWeight: '600'
                            }}>
                                <i className={`pi ${modeInfo.icon}`}></i>
                                {modeInfo.text}
                            </div>
                        </div>
                        <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                    </div>

                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <form autoComplete="off">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr 2fr', gap: '20px', alignItems: 'end' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                        marginBottom: '8px', color: '#374151'
                                    }}>
                                        🆔 ID
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%', height: '40px', fontSize: '0.95rem',
                                            padding: '0 12px', border: '2px solid #e5e7eb',
                                            borderRadius: '8px', backgroundColor: '#f9fafb'
                                        }}
                                        value={formData.id}
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                        marginBottom: '8px', color: '#374151'
                                    }}>
                                        🏢 Nombre <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%', height: '40px', fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={formData.nombre}
                                        onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                        disabled={mode === 'view'}
                                        placeholder="Ingrese el nombre de la sucursal"
                                    />
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                        marginBottom: '8px', color: '#374151'
                                    }}>
                                        🌐 IP <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%', height: '40px', fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={formData.ip}
                                        onChange={(e) => setFormData(prev => ({ ...prev, ip: e.target.value }))}
                                        disabled={mode === 'view'}
                                        placeholder="Ingrese la dirección IP"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Botones debajo de los inputs */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button icon="pi pi-search" label="Consultar" className="p-button-info p-button-raised" onClick={handleConsultar} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                                <Button icon="pi pi-plus" label="Nuevo" className="p-button-success p-button-raised" onClick={handleNuevo} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                                <Button icon="pi pi-pencil" label="Editar" className="p-button-warning p-button-raised" onClick={handleEditar} disabled={!formData.id} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !formData.id ? 0.5 : 1 }} />
                                <Button icon="pi pi-trash" label="Eliminar" className="p-button-danger p-button-raised" onClick={handleEliminar} disabled={!formData.id} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !formData.id ? 0.5 : 1 }} />
                            </>
                        ) : (
                            <>
                                <Button icon="pi pi-check" label="Guardar" className="p-button-success p-button-raised" onClick={handleGuardar} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                                <Button icon="pi pi-times" label="Cancelar" className="p-button-secondary p-button-raised" onClick={() => limpiarFormulario()} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sucursales;